import { useState } from "react";
import { Locate } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useValidateFedexAddress } from "@/api/fedex-address-validation.api";
import { FedexResolvedAddress } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import GoogleIcon from "@/assets/icons/google.svg?react";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";
import {
  ComparableAddressSnapshot,
  resolvedAddressToSnapshot,
  shouldShowStateOrProvince,
} from "./fedexAddressValidation.utils";
import { FedexAddressValidationComparisonModal } from "./fedexAddressValidationComparisonModal";

export const FedexAddressValidationActions = ({
  variant,
  disabled = false,
}: {
  variant: "delivery" | "shipper";
  disabled?: boolean;
}) => {
  const { t } = useTranslation("boExpeditionForm");
  const { getValues, setValue } = useFormContext<QuotationUpdateBoFormData>();
  const { mutateAsync: validateAddress, isPending: isValidateAddressPending } =
    useValidateFedexAddress();

  const [comparison, setComparison] = useState<{
    original: ComparableAddressSnapshot;
    resolved: FedexResolvedAddress;
    showStateOrProvince: boolean;
  } | null>(null);

  const readSnapshot = (): ComparableAddressSnapshot => {
    if (variant === "delivery") {
      const d = getValues("deliveryInfo");
      return {
        street: d.street ?? "",
        additionalInfo: d.additionalInfo ?? "",
        city: d.city ?? "",
        stateOrProvinceCode: d.stateOrProvinceCode ?? "",
        postalCode: d.postalCode ?? "",
        countryCode: d.countryCode ?? "",
      };
    }
    const s = getValues("shipperInfo");
    return {
      street: s.streetLines?.[0] ?? "",
      additionalInfo: s.streetLines?.[1] ?? "",
      city: s.city ?? "",
      stateOrProvinceCode: s.stateOrProvinceCode ?? "",
      postalCode: s.postalCode ?? "",
      countryCode: s.countryCode ?? "",
    };
  };

  const handleValidateAddress = async () => {
    const snapshot = readSnapshot();
    const showStateOrProvince = shouldShowStateOrProvince(snapshot.countryCode);
    if (!snapshot.street || !snapshot.city) {
      toast.error(t("fedexAddressValidation.streetAndCityRequired"));
      return;
    }
    try {
      const response = await validateAddress({
        addressesToValidate: [
          {
            address: {
              streetLines: [snapshot.street],
              city: snapshot.city,
              postalCode: snapshot.postalCode || "9999",
              countryCode: snapshot.countryCode,
              ...(showStateOrProvince && snapshot.stateOrProvinceCode
                ? { stateOrProvinceCode: snapshot.stateOrProvinceCode }
                : {}),
            },
          },
        ],
      });
      const resolved = response.output?.resolvedAddresses?.[0];
      if (!resolved) {
        toast.info(t("fedexAddressValidation.noSuggestion"));
        return;
      }
      setComparison({
        original: snapshot,
        resolved,
        showStateOrProvince,
      });
    } catch {
      toast.error(t("fedexAddressValidation.validationError"));
    }
  };

  const handleGoogleMapsRedirect = () => {
    const { street, city, postalCode, countryCode } = readSnapshot();
    const query = encodeURIComponent(`${street},${city},${postalCode},${countryCode}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  const closeComparison = () => setComparison(null);

  const handleUseSuggestedAddress = () => {
    if (!comparison) return;
    const suggested = resolvedAddressToSnapshot(comparison.resolved);
    const { showStateOrProvince } = comparison;
    const opts = { shouldDirty: true, shouldValidate: true } as const;
    if (variant === "delivery") {
      setValue("deliveryInfo.street", suggested.street, opts);
      setValue("deliveryInfo.additionalInfo", suggested.additionalInfo, opts);
      setValue("deliveryInfo.city", suggested.city, opts);
      setValue("deliveryInfo.postalCode", suggested.postalCode, opts);
      if (showStateOrProvince) {
        setValue("deliveryInfo.stateOrProvinceCode", suggested.stateOrProvinceCode || "", opts);
      }
    } else {
      setValue("shipperInfo.streetLines.0", suggested.street, opts);
      setValue("shipperInfo.streetLines.1", suggested.additionalInfo, opts);
      setValue("shipperInfo.city", suggested.city, opts);
      setValue("shipperInfo.postalCode", suggested.postalCode, opts);
      if (showStateOrProvince) {
        setValue("shipperInfo.stateOrProvinceCode", suggested.stateOrProvinceCode || "", opts);
      }
      setValue("shipperInfo.countryCode", suggested.countryCode, opts);
    }
    toast.success(t("fedexAddressValidation.updateSuccess"));
    closeComparison();
  };

  const treatCountryAsFixed = variant === "delivery";
  const modalTitle =
    variant === "delivery"
      ? t("fedexAddressValidation.deliveryResultTitle")
      : t("fedexAddressValidation.shipperResultTitle");

  const countrySupported = !(comparison?.resolved.attributes?.CountrySupported === "false");

  return (
    <>
      <div className="flex h-full flex-row items-center gap-2">
        <Button
          variant="tertiary"
          type="button"
          className="!h-full min-h-0 w-full rounded-lg px-3 py-0"
          onClick={handleGoogleMapsRedirect}
          disabled={disabled}
        >
          <GoogleIcon className="h-4 w-4 shrink-0 text-blue-600" />
        </Button>
        <Button
          variant="tertiary"
          type="button"
          className="!h-full min-h-0 w-full rounded-lg px-3 py-0"
          onClick={handleValidateAddress}
          disabled={disabled || isValidateAddressPending}
        >
          <Locate className="h-4 w-4 shrink-0" />
          <span className="hidden text-left leading-tight @2xl:block">
            {t("fedexAddressValidation.verifyButton")}
          </span>
        </Button>
      </div>
      {comparison ? (
        <FedexAddressValidationComparisonModal
          open
          countrySupported={countrySupported}
          title={<span className="text-base font-semibold">{modalTitle}</span>}
          treatCountryAsFixed={treatCountryAsFixed}
          showStateOrProvince={comparison.showStateOrProvince}
          onClose={closeComparison}
          original={comparison.original}
          resolved={comparison.resolved}
          onKeepOriginal={closeComparison}
          onUseSuggestion={handleUseSuggestedAddress}
        />
      ) : null}
    </>
  );
};
